import { DynamicForm } from "@shared/components/DynamicForm";
import { LocationMap } from "@shared/components/LocationMap";
import { PageHeader } from "@shared/components/PageHeader";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Input } from "@shared/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { useToast } from "@shared/ui/use-toast";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { format, parse } from "date-fns";
import { ArrowLeft, Download, ExternalLink, Info } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateFacility,
  useDepreciationSchedule,
  useFacilityDetails,
  useFacilityManagers,
  useFacilityStatuses,
  useFacilityTypes,
  useFacilityUtilityServices,
  useFacilityZones,
  useOperationalHours,
  useUpdateFacility,
} from "../../hooks";

export const AddFacility = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const remoteUtilityId = useRemoteUtilityId();
  const { id: facilityId } = useParams();

  const isEditMode = facilityId;
  const [formData, setFormData] = useState<any>({
    facilityName: "",
    facilityType: "",
    utilityService: "",
    operatingStatus: "",
    operationalHours: "",
    facilityManager: "",
    managerContact: "",
    addressSiteLabel: "",
    latitude: "",
    longitude: "",
    designCapacity: 0,
    serviceZone: "",
    commissionedDate: undefined,
    designUsefulLife: 0,
    annualBudget: 0,
    depreciationSchedule: "",
    documents: [],
    existingDocuments: [],
    description: "",
  });

  const [locationData, setLocationData] = useState({
    latitude: 0,
    longitude: 0,
    address: "",
  });

  useEffect(() => {
    setLocationData({
      latitude: formData?.latitude || "",
      longitude: formData?.longitude || "",
      address: formData?.addressSiteLabel || "",
    });
  }, [formData?.latitude, formData?.longitude, formData?.addressSiteLabel]);

  const { data: existingFacility } = useFacilityDetails(facilityId);

  const { data: facilityStatusesData } = useFacilityStatuses();
  const { data: operationalHoursData } = useOperationalHours();
  const { data: facilityTypesData } = useFacilityTypes();
  const { data: utilityServicesData } = useFacilityUtilityServices();
  const { data: zonesData } = useFacilityZones();
  const { data: managersData } = useFacilityManagers();
  const { data: depreciationScheduleData } = useDepreciationSchedule();

  const createFacilityMutation = useCreateFacility();
  const updateFacilityMutation = useUpdateFacility();

  const parseDate = (dateStr: string) => {
    try {
      if (!dateStr) return "";

      // Parse assuming input is in dd-MM-yyyy format
      const parsed = parse(dateStr, "dd-MM-yyyy", new Date());

      return format(parsed, "yyyy-MM-dd");
    } catch {
      return "";
    }
  };

  useEffect(() => {
    if (isEditMode) {
      const facility = existingFacility;
      const initialFormData = {
        facilityName: facility?.facilityName || "",
        facilityType: facility?.facilityTypeCode || "",
        utilityService: facility?.utilityService || "",
        operatingStatus: facility?.status || 0,
        operationalHours: facility?.operationalHours,
        facilityManager: facility?.userData?.userId,
        managerContact: facility?.userData?.email,
        addressSiteLabel: facility?.addressSiteLabel || "", // MOVED here
        latitude: facility?.latitude,
        longitude: facility?.longitude,
        designCapacity: parseFloat(facility?.overallDesignCapacity),
        serviceZone: facility?.serviceZoneCode || "",
        commissionedDate: parseDate(facility?.commissionedDate),
        designUsefulLife: facility?.designUsefulLife || 0,
        annualBudget: parseFloat(facility?.annualOmBudget || "0"),
        depreciationSchedule: facility?.depreciationSchedule || 0,
        documents: [],
        existingDocuments: facility?.uploadedDocuments || [],
        description: facility?.description || "",
      };

      setFormData(initialFormData);

      // Reset form with the new data
      if (form) {
        form.reset(initialFormData);
      }
    }
  }, [isEditMode, existingFacility]);

  const facilityTypeOptions = useMemo(() => {
    return (
      facilityTypesData?.result?.map((type: any) => ({
        value: type.code,
        label: type.name,
      })) || []
    );
  }, [facilityTypesData]);

  const utilityServiceOptions = useMemo(() => {
    return (
      utilityServicesData?.map((service: any) => ({
        value: service.name,
        label: service.name,
      })) || []
    );
  }, [utilityServicesData]);

  const operatingStatusOptions = useMemo(() => {
    return (
      facilityStatusesData?.result?.map((status: any) => ({
        value: status.key,
        label: status.value,
      })) || []
    );
  }, [facilityStatusesData]);

  const operationalHoursOptions = useMemo(() => {
    return (
      operationalHoursData?.result?.map((hours: any) => ({
        value: hours.key,
        label: hours.value,
      })) || []
    );
  }, [operationalHoursData]);

  const depreciationScheduleOptions = useMemo(() => {
    return (
      depreciationScheduleData?.result?.map((schedule: any) => ({
        value: schedule.key,
        label: schedule.value,
      })) || []
    );
  }, [depreciationScheduleData]);

  const facilityManagerOptions = useMemo(() => {
    return (
      managersData?.result?.map((manager: any) => ({
        value: Number(manager.user.id),
        label: `${manager.user.name} - ${manager.extraDataMap.department}`,
        email: manager.user.email,
        name: manager.user.name,
      })) || []
    );
  }, [managersData]);

  const serviceZoneOptions = useMemo(() => {
    return (
      zonesData?.result?.map((zone: any) => ({
        value: zone.code,
        label: zone.name,
      })) || []
    );
  }, [zonesData]);

  // Handle location change from map - this updates both locationData and formData
  const handleLocationChange = (lat: number, lng: number) => {
    const updatedLocationData = {
      ...locationData,
      latitude: lat,
      longitude: lng,
    };
    setLocationData(updatedLocationData);

    // Also update formData
    setFormData((prev) => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  // Handle manual coordinate input
  const handleLatitudeChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -90 || numValue > 90) {
      toast({
        title: "Invalid Latitude",
        description: "Latitude must be between -90 and 90 degrees.",
        variant: "destructive",
      });
      return;
    }

    const updatedLocationData = { ...locationData, latitude: numValue };
    setLocationData(updatedLocationData);

    // Also update formData
    setFormData((prev) => ({ ...prev, latitude: numValue }));
  };

  const handleLongitudeChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < -180 || numValue > 180) {
      toast({
        title: "Invalid Longitude",
        description: "Longitude must be between -180 and 180 degrees.",
        variant: "destructive",
      });
      return;
    }

    const updatedLocationData = { ...locationData, longitude: numValue };
    setLocationData(updatedLocationData);

    // Also update formData
    setFormData((prev) => ({ ...prev, longitude: numValue }));
  };

  const handleAddressChange = (address: string) => {
    const updatedLocationData = { ...locationData, address };
    setLocationData(updatedLocationData);

    // Also update formData
    setFormData((prev) => ({
      ...prev,
      addressSiteLabel: address,
    }));
  };

  const formFields: FormField[] = [
    // Facility Identification & Role Section
    {
      name: "facilityName",
      type: "text",
      label: "Facility Name",
      group: "Facility Identification & Role",
      placeholder: "e.g., KS Water Treatment Plant - South Zone",
      required: true,
      groupColorNeeded: false,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "The full, descriptive name of the facility.",
    },
    {
      name: "facilityType",
      type: "select",
      label: "Facility Type",
      group: "Facility Identification & Role",
      placeholder: "Select facility type",
      required: true,
      options: facilityTypeOptions,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "Select the primary function of this facility.",
    },
    {
      name: "utilityService",
      type: "select",
      label: "Utility Service",
      group: "Facility Identification & Role",
      placeholder: "Select utility service",
      required: true,
      options: utilityServiceOptions,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "The utility service this facility provides.",
    },
    {
      name: "operatingStatus",
      type: "select",
      label: "Operating Status",
      group: "Facility Identification & Role",
      placeholder: "Select operating status",
      required: true,
      options: operatingStatusOptions,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "The current operational status of the facility.",
    },
    {
      name: "operationalHours",
      type: "select",
      label: "Operational Hours",
      group: "Facility Identification & Role",
      placeholder: "Select operational hours",
      required: true,
      options: operationalHoursOptions,
      className: "col-span-2 mt-3",
      showHelperTooltip: true,
      tooltipText: "The standard operating schedule for this facility.",
    },

    // Location & Responsibility Section
    {
      name: "facilityManager",
      type: "select",
      label: "Facility Manager / Lead Contact",
      group: "Location & Responsibility",
      groupColorNeeded: false,
      placeholder: "Select facility manager",
      required: true,
      options: facilityManagerOptions,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText: "The person responsible for managing this facility.",
    },
    {
      name: "managerContact",
      type: "text",
      label: "Manager Contact Details",
      group: "Location & Responsibility",
      placeholder: "Contact details will be auto-filled",
      required: false,
      className: "col-span-1 mt-3",
      disabled: true,
      showHelperTooltip: true,
      tooltipText: "Contact information of the selected facility manager.",
    },

    {
      name: "location_map",
      type: "text",
      label: "Facility Location",
      group: "Location & Responsibility",
      required: false,
      className: "col-span-2 mt-3",
      customRender: () => (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="flex items-center text-xs font-medium text-gray-600 mb-1 gap-1">
                Latitude
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        size={14}
                        className="text-gray-400 cursor-pointer"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      The geographic latitude of the facility. Auto-filled from
                      the map or can be entered manually.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., 12.8456"
                value={locationData.latitude || ""}
                onChange={(e) => handleLatitudeChange(e.target.value)}
                className="w-full"
              />
            </div>
            <div>
              <label className="flex items-center text-xs font-medium text-gray-600 mb-1 gap-1">
                Longitude
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        size={14}
                        className="text-gray-400 cursor-pointer"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      The geographic longitude of the facility. Auto-filled from
                      the map or can be entered manually.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </label>
              <Input
                type="number"
                step="any"
                placeholder="e.g., 77.6618"
                value={locationData.longitude || ""}
                onChange={(e) => handleLongitudeChange(e.target.value)}
                className="w-full"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="flex items-center text-xs font-medium text-gray-600 mb-1 gap-1">
              Address/Site Label
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info size={14} className="text-gray-400 cursor-pointer" />
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    The physical street address or a descriptive site label for
                    the facility's location.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </label>
            <Input
              type="text"
              placeholder="e.g., 123 Water Treatment Road, Industrial Area"
              value={locationData.address || ""}
              className="w-full bg-gray-50"
              onChange={(e) => handleAddressChange(e.target.value)}
            />
          </div>

          <LocationMap
            latitude={locationData.latitude}
            longitude={locationData.longitude}
            onLocationChange={handleLocationChange}
            onAddressChange={handleAddressChange}
          />
        </div>
      ),
      showHelperTooltip: true,
      tooltipText: "The geographical location of the facility.",
    },
    {
      name: "description",
      type: "textarea",
      label: "Description / Purpose",
      group: "Location & Responsibility",
      placeholder: "Describe the system's purpose and key characteristics...",
      required: false,
      showHelperTooltip: true,
      tooltipText:
        "Description/Purpose Write a comprehensive description of the facility's function, capacity, key components, and any special operational characteristics that would help others understand its role in your infrastructure. Include details about processes, capacity ratings, unique features, and any operational constraints or requirements.",
      rows: 4,
      defaultValue: formData?.description || "",
      className: "col-span-2 mt-3",
    },

    // High-Level Operational Parameters Section
    {
      name: "designCapacity",
      type: "decimal",
      label: "Overall Design Capacity (MLD)",
      group: "High-Level Operational Parameters",
      groupColorNeeded: false,
      placeholder: "e.g., 600.00",
      required: true,
      showHelperTooltip: true,
      className: "col-span-1 mt-3",
      tooltipText:
        "The maximum design capacity of the facility in Megaliters per Day (MLD).",
    },
    {
      name: "serviceZone",
      type: "select",
      label: "Service Zone",
      group: "High-Level Operational Parameters",
      placeholder: "Select service zone",
      required: false,
      showHelperTooltip: true,
      options: serviceZoneOptions,
      className: "col-span-1 mt-3",
      tooltipText:
        "The specific service zone this facility supports. Used for operational and customer impact analysis.",
    },

    // Financial & Lifecycle Section
    {
      name: "commissionedDate",
      label: "Commissioned Date",
      group: "Financial & Lifecycle",
      groupColorNeeded: false,
      placeholder: "Select commissioned date",
      required: true,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      type: "datepicker",
      allowFuture: false,
      tooltipText:
        "The date the facility was officially placed into service. This is used as the start date for its operational life.",
    },
    {
      name: "designUsefulLife",
      type: "number",
      label: "Design Useful Life (Years)",
      group: "Financial & Lifecycle",
      placeholder: "e.g., 25",
      showHelperTooltip: true,
      required: true,
      className: "col-span-1 mt-3",
      tooltipText:
        "The expected number of years this facility is designed to operate before requiring major overhaul or replacement.",
    },
    {
      name: "annualBudget",
      type: "number",
      label: "Annual Facility O&M Budget ($)",
      group: "Financial & Lifecycle",
      placeholder: "e.g., 135000.00",
      showHelperTooltip: true,
      required: true,
      step: 1000,
      className: "col-span-1 mt-3",
      tooltipText:
        "The budgeted annual cost for all operations and maintenance activities for this facility.",
    },
    {
      name: "depreciationSchedule",
      type: "select",
      label: "Depreciation Schedule",
      group: "Financial & Lifecycle",
      placeholder: "Select depreciation schedule",
      required: true,
      options: depreciationScheduleOptions,
      className: "col-span-1 mt-3",
      showHelperTooltip: true,
      tooltipText:
        "The frequency of depreciation calculations for this facility.",
    },
    {
      name: "existingDocuments",
      type: "hidden",
      label: "Existing Documents",
      group: "Documentation",
      groupColorNeeded: false,
      required: false,
      className: "col-span-2 mt-3",
      customRender: () => {
        if (
          !isEditMode ||
          !formData.existingDocuments ||
          formData.existingDocuments.length === 0
        ) {
          return null;
        }

        return (
          <div className="space-y-3">
            <div className="text-sm text-gray-600 mb-3">
              These are documents currently associated with this facility. To
              manage these files, contact your administrator.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {formData.existingDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
                >
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    <div className="flex-shrink-0" title={doc.fileName}>
                      {doc.fileName.length > 10
                        ? doc.fileName.substring(0, 10) + "..."
                        : doc.fileName}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => window.open(doc.fileUrl, "_blank")}
                      className="p-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </button>
                    <a
                      href={doc.fileUrl}
                      download={doc.fileName}
                      className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                      title="Download file"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-xs text-gray-500">
              {formData.existingDocuments.length} existing document(s)
            </div>
          </div>
        );
      },
      showHelperTooltip: false,
    },

    // Documentation Section
    {
      name: "documents",
      type: "multi-file",
      label: "Facility-Wide Documents",
      className: "col-span-2",
      group: "Documentation",
      placeholder: "Upload facility documents",
      required: false,
      multiple: true,
      accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
      maxSize: 100,
      helperText:
        "Examples: Site Plans, Environmental Permits, Geotechnical Reports, Security Plans",
      classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: formData,
  });

  const handleSubmit = async () => {
    const currentFormValues = form.getValues();

    const apiFormData = new FormData();

    apiFormData.append("facility_name", currentFormValues?.facilityName || "");
    apiFormData.append(
      "utility_service",
      currentFormValues?.utilityService || ""
    );
    apiFormData.append(
      "facility_type_code",
      currentFormValues?.facilityType || ""
    );
    apiFormData.append(
      "service_zone_code",
      currentFormValues?.serviceZone || ""
    );

    // Fix: Convert number values to strings properly
    apiFormData.append(
      "status",
      currentFormValues?.operatingStatus !== undefined
        ? currentFormValues.operatingStatus.toString()
        : ""
    );

    apiFormData.append(
      "overall_design_capacity",
      (currentFormValues?.designCapacity || 0).toString()
    );
    apiFormData.append(
      "commissioned_date",
      currentFormValues?.commissionedDate || ""
    );
    apiFormData.append(
      "design_useful_life",
      (currentFormValues?.designUsefulLife || 0).toString()
    );
    apiFormData.append(
      "annual_om_budget",
      (currentFormValues?.annualBudget || 0).toString()
    );
    apiFormData.append(
      "facility_manager_id",
      currentFormValues?.facilityManager
        ? currentFormValues.facilityManager.toString()
        : ""
    );
    if (locationData.latitude && locationData.longitude) {
      apiFormData.append(
        "latitude",
        (currentFormValues?.latitude || locationData.latitude || "").toString()
      );
      apiFormData.append(
        "longitude",
        (
          currentFormValues?.longitude ||
          locationData.longitude ||
          ""
        ).toString()
      );
    }

    // Fix: Convert operational_hours number to string
    apiFormData.append(
      "operational_hours",
      currentFormValues?.operationalHours !== undefined
        ? currentFormValues.operationalHours.toString()
        : ""
    );

    // Fix: Convert depreciation_schedule number to string
    apiFormData.append(
      "depreciation_schedule",
      currentFormValues?.depreciationSchedule !== undefined
        ? currentFormValues.depreciationSchedule.toString()
        : ""
    );

    // Optional fields
    if (currentFormValues?.description) {
      apiFormData.append("description", currentFormValues?.description);
    }
    if (currentFormValues?.addressSiteLabel || locationData.address) {
      apiFormData.append(
        "address_site_label",
        currentFormValues?.addressSiteLabel || locationData.address
      );
    }

    // Add documents
    if (
      Array.isArray(currentFormValues?.documents) &&
      currentFormValues?.documents.length > 0
    ) {
      currentFormValues?.documents.forEach((doc) => {
        apiFormData.append("documents", doc);
      });
    }

    try {
      if (isEditMode && facilityId) {
        await updateFacilityMutation.mutateAsync({
          facilityId: facilityId,
          formData: apiFormData,
        });
      } else {
        await createFacilityMutation.mutateAsync(apiFormData);
      }

      navigate("/ax/asset-registry/facilities");
    } catch (error) {
      toast({
        title: "Error",
        description: error?.message || "Failed to create facility",
        variant: "destructive",
      });
    }
  };
  const handleCancel = () => {
    navigate("/ax/asset-registry/facilities");
  };

  const handleFieldChange = (field: string, value: any) => {
    switch (field) {
      case "facilityManager":
        if (!value) {
          // Use timeout to avoid stack overflow
          setTimeout(() => {
            form.setValue("managerContact", "");
          }, 0);
          return;
        }

        const selected = facilityManagerOptions.find(
          (opt) => opt.value === Number(value)
        );
        if (selected) {
          // Use timeout to avoid stack overflow
          setTimeout(() => {
            form.setValue("managerContact", selected.email || "");
          }, 0);
        }
        return;
    }
  };

  return (
    <div className="p-4">
      <div>
        <div className="mb-4 flex items-center gap-4">
          <ArrowLeft
            className="w-5 h-5 cursor-pointer text-gray-700"
            onClick={() => navigate("/ax/asset-registry/facilities")}
          />
          <div className="flex flex-col">
            <PageHeader
              title={isEditMode ? `Edit Facility` : "Add New Facility"}
              subtitle={
                isEditMode
                  ? `Update facility record`
                  : "Create a new facility in the utility"
              }
            />
          </div>
        </div>

        <div className="bg-white">
          <div>
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleCancel}
              onFieldChange={handleFieldChange}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: true,
                showSubmitButton: true,
                submitButtonText: createFacilityMutation.isPending
                  ? isEditMode
                    ? "updating.."
                    : "Creating..."
                  : isEditMode
                  ? "Update Facility"
                  : "Create Facility",
                resetButtonText: "Cancel",
                spacing: "comfortable",
                classes: FIELD_CLASSES,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
